import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { App, Button, Drawer, Form, Input, InputNumber, Modal, Select, Space, Switch, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";
import {
  activatePostingProfile,
  deletePostingProfile,
  getPostingProfile,
  listPostingProfiles,
  listPostingRules,
  savePostingProfile,
  savePostingRules,
  type PostingProfileRow,
  type PostingRuleRow
} from "../../api/postingProfileApi";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { DataGrid } from "../../shared/components/DataGrid";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { SmartLookupField } from "../../shared/lookup/SmartLookupField";
import { getApiErrorMessage } from "../../shared/utils/apiError";

const moduleOptions = ["BillEntry", "DailyEntry", "CashEntry", "StatementEntry", "Payment", "Receipt", "bill-payment"].map((value) => ({ value, label: value }));
const voucherOptions = ["Debit", "Credit", "SalesInvoice", "PurchaseInvoice", "ReceiptVoucher", "PaymentVoucher", "JournalEntry", "Payment"].map((value) => ({ value, label: value }));
const roleOptions = ["Expense", "Party", "Tax", "Cash", "Bank", "Ledger", "Revenue"].map((value) => ({ value, label: value }));
const entrySideOptions = ["Debit", "Credit"].map((value) => ({ value, label: value }));
const amountSourceOptions = ["TransactionAmount", "NetAmount", "TotalAmount", "TaxAmount", "CGST", "SGST", "IGST", "TDS", "TOTAL", "WAGES", "Amount", "RoundOff", "CustomField"].map((value) => ({ value, label: value }));
const ledgerSourceOptions = ["StaticLedger", "Party", "TaxLedger", "BankAccount"].map((value) => ({ value, label: value }));
const taxTypeOptions = ["CGST", "SGST", "IGST", "TDS"].map((value) => ({ value, label: value }));

export function PostingProfilesPage() {
  const { message } = App.useApp();
  const [profileForm] = Form.useForm();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [editingProfile, setEditingProfile] = useState<PostingProfileRow | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [rulesProfile, setRulesProfile] = useState<PostingProfileRow | null>(null);

  const queryParams = useMemo(() => ({ pageNumber: page, pageSize, search: search || undefined }), [page, pageSize, search]);
  const profiles = useQuery({
    queryKey: ["posting-profiles", queryParams],
    queryFn: () => listPostingProfiles(queryParams)
  });

  const openProfileModal = async (row?: PostingProfileRow) => {
    setEditingProfile(row ?? null);
    setProfileModalOpen(true);
    if (row?.id) {
      const details = await getPostingProfile(row.id);
      profileForm.setFieldsValue(details);
    } else {
      profileForm.resetFields();
      profileForm.setFieldsValue({ isActive: true, moduleCode: "BillEntry" });
    }
  };

  const profileColumns: ColumnsType<PostingProfileRow> = [
    {
      key: "profile",
      title: "Posting Profile",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space wrap size={6}>
            <Typography.Text strong>{row.moduleCode}</Typography.Text>
            <Typography.Text type="secondary">/</Typography.Text>
            <Typography.Text strong>{row.transactionTypeCode}</Typography.Text>
          </Space>
          <Space size={6}>
            <Tag color={row.isActive ? "green" : "default"}>{row.isActive ? "Active" : "Inactive"}</Tag>
            <Tag color={row.isCompiled ? "blue" : "gold"}>{row.isCompiled ? "Compiled" : "Not Compiled"}</Tag>
          </Space>
        </Space>
      )
    },
    { title: "Compiled On", dataIndex: "compiledOn", width: 180, render: (value) => value ? new Date(value).toLocaleString() : "-" },
    {
      key: "actions",
      title: "",
      width: 210,
      fixed: "right",
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" onClick={() => setRulesProfile(row)}>Rules</Button>
          <Button size="small" icon={<ThunderboltOutlined />} onClick={async () => {
            try {
              await activatePostingProfile(row.id);
              message.success("Posting profile activated");
              profiles.refetch();
            } catch (err) {
              message.error(getApiErrorMessage(err, "Unable to activate profile"));
            }
          }} />
          <Button size="small" icon={<EditOutlined />} onClick={() => openProfileModal(row)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDeletePreview({
            entityName: "Posting Profile",
            fields: [
              { label: "Module", value: row.moduleCode },
              { label: "Transaction", value: row.transactionTypeCode },
              { label: "Active", value: row.isActive ? "Yes" : "No", type: "tag" }
            ],
            onConfirm: async () => {
              await deletePostingProfile(row.id);
              message.success("Posting profile deleted");
              profiles.refetch();
            }
          })} />
        </Space>
      )
    }
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total: profiles.data?.total ?? 0,
    showSizeChanger: true,
    onChange: (nextPage, nextPageSize) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    }
  };

  return (
    <div>
      <PageHeaderActions
        title="Posting Profiles"
        subtitle="Journal posting rules migrated from the MVC parent-child PostingProfile screen."
        right={
          <>
            <Input.Search allowClear placeholder="Search module or transaction" value={search} onChange={(event) => { setPage(1); setSearch(event.target.value); }} />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openProfileModal()}>Create</Button>
            <Button icon={<ReloadOutlined />} onClick={() => profiles.refetch()}>Refresh</Button>
          </>
        }
      />
      <DataGrid rows={profiles.data?.items ?? []} columns={profileColumns} loading={profiles.isLoading || profiles.isFetching} pagination={pagination} tableId="posting-profiles" onRowDoubleClick={(row) => setRulesProfile(row)} />

      <Modal
        title={editingProfile ? "Edit Posting Profile" : "Create Posting Profile"}
        open={profileModalOpen}
        onCancel={() => setProfileModalOpen(false)}
        onOk={() => profileForm.submit()}
      >
        <Form form={profileForm} layout="vertical" onFinish={async (values) => {
          try {
            await savePostingProfile(values, editingProfile?.id);
            message.success("Posting profile saved");
            setProfileModalOpen(false);
            profiles.refetch();
          } catch (err) {
            message.error(getApiErrorMessage(err, "Unable to save posting profile"));
          }
        }}>
          <Form.Item name="moduleCode" label="Module" rules={[{ required: true }]}>
            <Select showSearch options={moduleOptions} />
          </Form.Item>
          <Form.Item name="transactionTypeCode" label="Transaction Type" rules={[{ required: true }]}>
            <Select showSearch options={voucherOptions} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <PostingRulesDrawer profile={rulesProfile} onClose={() => setRulesProfile(null)} />
    </div>
  );
}

function PostingRulesDrawer({ profile, onClose }: { profile: PostingProfileRow | null; onClose: () => void }) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<PostingRuleRow[]>([]);
  const [saving, setSaving] = useState(false);
  const open = Boolean(profile);
  const rules = useQuery({
    queryKey: ["posting-profile-rules", profile?.id],
    enabled: open && Boolean(profile?.id),
    queryFn: () => listPostingRules(profile!.id)
  });

  useEffect(() => {
    if (!open) return;
    setRows((rules.data ?? []).map((row, index) => ({ ...row, key: String(row.id ?? `existing-${index}`) })));
  }, [open, rules.data]);

  const update = (key: string, patch: Partial<PostingRuleRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
  };

  const addRule = () => {
    if (!profile) return;
    const key = `new-${Date.now()}`;
    setRows((current) => [
      ...current,
      {
        key,
        id: null,
        postingProfileId: profile.id,
        sequence: current.length + 1,
        role: "Ledger",
        entrySide: "Debit",
        amountSource: "TransactionAmount",
        ledgerSourceCode: "StaticLedger",
        staticLedgerId: null
      }
    ]);
  };

  const columns: ColumnsType<PostingRuleRow> = [
    { title: "Seq", dataIndex: "sequence", width: 82, render: (_, row) => <InputNumber min={1} value={row.sequence} onChange={(value) => update(row.key!, { sequence: Number(value ?? 1) })} /> },
    { title: "Role", dataIndex: "role", width: 140, render: (_, row) => <Select value={row.role} options={roleOptions} onChange={(value) => update(row.key!, { role: value })} /> },
    { title: "Side", dataIndex: "entrySide", width: 130, render: (_, row) => <Select value={row.entrySide} options={entrySideOptions} onChange={(value) => update(row.key!, { entrySide: value })} /> },
    { title: "Amount", dataIndex: "amountSource", width: 180, render: (_, row) => <Select showSearch value={row.amountSource} options={amountSourceOptions} onChange={(value) => update(row.key!, { amountSource: value })} /> },
    { title: "Ledger Source", width: 170, render: (_, row) => <Select value={row.ledgerSourceCode ?? row.ledgerSource} options={ledgerSourceOptions} onChange={(value) => update(row.key!, { ledgerSourceCode: value })} /> },
    {
      title: "Static Ledger",
      width: 220,
      render: (_, row) => (row.ledgerSourceCode ?? row.ledgerSource) === "StaticLedger" ? (
        <SmartLookupField lookupKey="accountLedger" value={row.staticLedgerId ?? undefined} onChange={(value) => update(row.key!, { staticLedgerId: Number(value.id) || null })} />
      ) : <span className="muted-cell">-</span>
    },
    {
      title: "Tax",
      width: 130,
      render: (_, row) => (row.ledgerSourceCode ?? row.ledgerSource) === "TaxLedger" ? (
        <Select allowClear value={row.taxTypeCode ?? row.taxType ?? undefined} options={taxTypeOptions} onChange={(value) => update(row.key!, { taxTypeCode: value })} />
      ) : <span className="muted-cell">-</span>
    },
    {
      title: "",
      width: 64,
      render: (_, row) => <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))} />
    }
  ];

  return (
    <Drawer
      title={profile ? `Posting Rules - ${profile.moduleCode}/${profile.transactionTypeCode}` : "Posting Rules"}
      width="min(1180px, 96vw)"
      open={open}
      onClose={onClose}
      extra={
        <Space>
          <Button icon={<PlusOutlined />} onClick={addRule}>Add Rule</Button>
          <Button type="primary" loading={saving} onClick={async () => {
            try {
              setSaving(true);
              await savePostingRules(rows);
              message.success("Rules saved");
              rules.refetch();
            } catch (err) {
              message.error(getApiErrorMessage(err, "Unable to save posting rules"));
            } finally {
              setSaving(false);
            }
          }}>Save Rules</Button>
        </Space>
      }
    >
      <PersistedTable tableId={`posting-rules-${profile?.id ?? "none"}`} rowKey={(row) => row.key ?? String(row.id)} size="small" bordered loading={rules.isLoading} dataSource={rows} columns={columns} pagination={false} scroll={{ x: 1120 }} />
    </Drawer>
  );
}
